import React, { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  AppShell,
  Header,
  Navbar,
  ScrollArea,
  Tooltip,
} from "@mantine/core";
import { Link } from "react-router-dom";
import {
  Bell,
  File,
  Home,
  LayoutSidebarLeftCollapse,
  LayoutSidebarRightCollapse,
  List,
  Lock,
  Message,
  Paperclip,
  Plus,
  Template,
} from "tabler-icons-react";
import { LinksGroup } from "../../components/NavbarLinkGroup";
import AppHeader from "../../components/Header/AppHeader";
import { selectUserData } from "../../redux/selectors";
import { useSelector } from "react-redux";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import { getUnseenNotifications } from "../../utilities/apis/users";
import { assetsPrefix } from "../../utilities/utilities";
import { NAV_ACCESS, canNavAccess } from "../../utilities/navAccess";

const SIDEBAR_WIDTH = 248;
const SIDEBAR_COLLAPSED_WIDTH = 68;
const HEADER_HEIGHT = 60;

const buildNavItems = ({ dm, loginAs, unseenCount }) => {
  const id = loginAs?.id;
  const items = [];

  if (!dm) {
    if (canNavAccess(NAV_ACCESS.dashboard, id)) {
      items.push({
        label: "dashboard",
        icon: Home,
        link: "/app/agreements_manager",
      });
    }

    if (canNavAccess(NAV_ACCESS.notifications, id)) {
      items.push({
        label: "notifications",
        icon: Bell,
        link: "/app/agreements_manager/notifications",
        ...(unseenCount > 0 && {
          hasNumber: unseenCount > 99 ? "99+" : String(unseenCount),
        }),
      });
    }

    if (canNavAccess(NAV_ACCESS.myAgreements, id)) {
      items.push({
        label: "my agreements",
        icon: Paperclip,
        link: "/app/agreements_manager/agreements-list",
      });
    }

    if (canNavAccess(NAV_ACCESS.newAgreement, id)) {
      items.push({
        label: "new agreement",
        icon: Plus,
        initiallyOpened: false,
        links: [
          {
            label: "individual agreement",
            link: "/app/agreements_manager/agreements/new",
          },
          {
            label: "bulk agreements",
            link: "/app/agreements_manager/agreements/new-bulk",
          },
          {
            label: "legacy agreement",
            link: "/app/agreements_manager/agreements/new-legacy-agreement",
          },
          {
            label: "bulk legacy agreements",
            link: "/app/agreements_manager/agreements/bulk-legacy-agreement",
          },
        ],
      });
    }

    if (canNavAccess(NAV_ACCESS.docsLocker, id)) {
      items.push({
        label: "docs locker",
        icon: Lock,
        link: "/app/agreements_manager/docs-locker",
      });
    }

    if (canNavAccess(NAV_ACCESS.documentChat, id)) {
      items.push({
        label: "document chat",
        icon: Message,
        link: "/app/agreements_manager/document-chat",
      });
    }

    if (canNavAccess(NAV_ACCESS.templates, id)) {
      items.push({
        label: "templates",
        icon: Template,
        initiallyOpened: false,
        links: [
          {
            label: "agreements",
            link: "/app/agreements_manager/templates/agreement",
          },
          {
            label: "clauses",
            link: "/app/agreements_manager/templates/clause",
          },
        ],
      });
    }

    if (canNavAccess(NAV_ACCESS.obligations, id)) {
      items.push({
        label: "obligations",
        icon: List,
        link: "/app/agreements_manager/obligations",
      });
    }
  } else {
    items.push({
      label: "documents",
      icon: File,
      link: "/app/corporatedocuments",
    });
  }

  return items;
};

const MainAppAgreementsManager = ({ appContent, dm = false }) => {
  const currentUser = useSelector(selectUserData) || {};
  const loginAs = currentUser.loginAs;
  const [unseenCount, setUnseenCount] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const sidebarWidth = sidebarCollapsed
    ? SIDEBAR_COLLAPSED_WIDTH
    : SIDEBAR_WIDTH;

  useEffect(() => {
    const loadUnseenCount = async () => {
      const response = await apiWrapWithErrorWithData(getUnseenNotifications());
      if (response?.success) {
        setUnseenCount(response.count ?? 0);
      }
    };
    loadUnseenCount();
  }, []);

  const navItems = useMemo(
    () => buildNavItems({ dm, loginAs, unseenCount }),
    [dm, loginAs, unseenCount],
  );

  return (
    <AppShell
      header={
        <Header height={HEADER_HEIGHT}>
          <div className="flex h-[60px] w-full items-stretch border-b border-slate-200">
            <div
              className="flex shrink-0 items-center justify-between gap-2 border-r border-slate-200 bg-slate-50 px-3 transition-[width] duration-200 ease-in-out"
              style={{ width: sidebarWidth }}
            >
              <Link
                to="/app/choose_manager"
                className="flex min-w-0 items-center"
              >
                <img
                  className={
                    sidebarCollapsed
                      ? "h-7 w-7 object-contain"
                      : "h-8 w-auto max-w-[160px] object-contain"
                  }
                  src={`${assetsPrefix}/images/trueCounselLogo.png`}
                  alt="TrueCounsel"
                />
              </Link>
              <Tooltip
                label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                position="right"
              >
                <ActionIcon
                  variant="subtle"
                  className="shrink-0 text-[#46BDE1]"
                  onClick={() => setSidebarCollapsed((prev) => !prev)}
                  aria-label="Toggle sidebar"
                >
                  {sidebarCollapsed ? (
                    <LayoutSidebarRightCollapse size={18} color="#46BDE1" />
                  ) : (
                    <LayoutSidebarLeftCollapse size={18} color="#46BDE1" />
                  )}
                </ActionIcon>
              </Tooltip>
            </div>
            <div className="min-w-0 flex-1 bg-white">
              <AppHeader hideLogo />
            </div>
          </div>
        </Header>
      }
      navbar={
        <Navbar width={{ base: sidebarWidth }} p={0}>
          <Navbar.Section grow component={ScrollArea} className="px-2 py-2">
            {navItems.map((item) => (
              <LinksGroup
                {...item}
                key={item.label}
                collapsed={sidebarCollapsed}
              />
            ))}
          </Navbar.Section>
        </Navbar>
      }
    >
      <div className="min-h-[calc(100dvh-80px)] bg-white shadow-sm">
        {appContent}
      </div>
    </AppShell>
  );
};

export default MainAppAgreementsManager;
